import Cookie from 'js-cookie';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LocaleSelect, { redirectToLocaleVariant } from '@tmh-website/components/global/LocaleSelect';
import { PageContext, ProjectContext, ScrollContext, useMediaQuery } from '@tmh-website/shared';
import { Color } from '@tmh-website/shared/colors';
import { Locale, LocalePrefix, LocaleToPrefixMap, Project } from '@tmh-website/shared/const';
import StyledGlobal from '@tmh-website/styles/global';
import X from '@tmh-website/components/icons/phosphor-extension/icons/X';

import * as Styled from './LocaleSwitchBanner.styles';
import { LocaleSwitchBannerThemeColor, localeSwitchBannerThemes } from './LocaleSwitchBanner.theme';

/**
 * Returns the locale prefix that the user has set in the browser language or closest match
 */
function getSimilarLocalePrefix(isEnergyProject: boolean): LocalePrefix | undefined {
  const navigatorLocale = navigator.language;
  switch (navigatorLocale) {
    case 'de':
      return LocaleToPrefixMap[Locale.De];
    case 'de-CH':
      return LocaleToPrefixMap[Locale.DeCH];
    case 'en-US':
      return !isEnergyProject ? LocaleToPrefixMap[Locale.EnUS] : undefined;
    case 'fr-CH':
      return LocaleToPrefixMap[Locale.FrCH];
    case 'fr':
      return LocaleToPrefixMap[Locale.Fr];
  }

  const similarLocale = Object.values(Locale).find(locale => {
    if (locale.split('-')[0].includes(navigatorLocale.split('-')[0])) {
      return locale;
    }
  });

  return similarLocale ? LocaleToPrefixMap[similarLocale] : undefined;
}

interface LocaleSwitchBannerProps {
  locale: LocalePrefix;
  themeColor: LocaleSwitchBannerThemeColor;
  /**
   * Force the banner to be visible. Used for e.g. storybook.
   */
  forceVisible?: boolean;
}

const LocaleSwitchBanner = ({
  locale,
  themeColor = Color.Blue_1000_Deep,
  forceVisible,
  ...props
}: LocaleSwitchBannerProps) => {
  const { t } = useTranslation();
  const { localeSwitchBannerIsVisible, setLocaleSwitchBannerIsVisible } = useContext(ScrollContext);
  const [recommendedLocale, setRecommendedLocale] = useState<LocalePrefix>();
  const isSmOrLower = useMediaQuery(`(max-width: ${650 - 0.02}px)`);
  const page = useContext(PageContext);
  const { project } = useContext(ProjectContext);

  useEffect(() => {
    const newRecommendedLocale = getSimilarLocalePrefix(project === Project.Energy);
    const preferredLocale = Cookie.get('tmh_preferred-locale');
    setRecommendedLocale(newRecommendedLocale);
    // We don't show the banner if the locale matches the cookie settings or our recommended locale
    setLocaleSwitchBannerIsVisible(locale !== newRecommendedLocale && locale !== preferredLocale);
  }, [locale]);

  function closeBanner(preferedLocale: LocalePrefix) {
    setLocaleSwitchBannerIsVisible(false);
    Cookie.set('tmh_preferred-locale', preferedLocale, { expires: 365 });
    redirectToLocaleVariant(page, locale, preferedLocale);
    // Do not continue with the LocaleSelect's onSelect function
    return false;
  }

  if (!locale || (!localeSwitchBannerIsVisible && !forceVisible)) {
    return null;
  }

  return (
    <Styled.Main $themeColor={themeColor} {...props} data-cy='localSwitchBanner'>
      <StyledGlobal.WrapperLarge>
        <Styled.Container>
          <Styled.Text $style='uiTextSmall' as='p' $noMargin>
            {t('global:switchLocale')}
          </Styled.Text>

          <Styled.ButtonsContainer>
            <LocaleSelect
              themeColor={localeSwitchBannerThemes[themeColor].formElementThemeColor}
              customLabelLocale={recommendedLocale}
              locale={locale}
              isSmall={isSmOrLower}
              customOnSelect={locale => closeBanner(locale)}
            />
            <Styled.AdjutedButton
              themeColor={localeSwitchBannerThemes[themeColor].buttonThemeColor}
              hoverThemeColor={localeSwitchBannerThemes[themeColor].buttonHoverThemeColor}
              onClick={() => closeBanner(recommendedLocale ?? locale)}
              data-cy='localSwitchBannerCloseButton'
              aria-label={t('global:continue') as string}>
              {t('global:continue')}
            </Styled.AdjutedButton>
            <Styled.AdjustedButtonIcon
              themeColor={themeColor}
              onClick={() => closeBanner(locale)}
              aria-label={t('global:close') as string}>
              <X size={40} isSmall />
            </Styled.AdjustedButtonIcon>
          </Styled.ButtonsContainer>
        </Styled.Container>
      </StyledGlobal.WrapperLarge>
    </Styled.Main>
  );
};

export default LocaleSwitchBanner;
